import { t } from 'i18next'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import {
  type LoaderFunctionArgs,
  type HeadersFunction,
  type LinksFunction,
  type MetaFunction,
  data,
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLoaderData,
  useLocation,
} from 'react-router'
import { HoneypotProvider } from 'remix-utils/honeypot/react'
import { AppHeader } from './components/app-header.tsx'
import { Confetti } from './components/confetti.tsx'
import { GeneralErrorBoundary } from './components/error-boundary.tsx'
import { Footer } from './components/footer.tsx'
import { GoogleAnalytics } from './components/google-analytics.tsx'
import { EpicProgress } from './components/progress-bar.tsx'
import { EpicToaster } from './components/toaster.tsx'
import { href as iconsHref } from './components/ui/icon.tsx'

import {
  getLocale,
  i18nextMiddleware,
  localeCookie,
} from './middleware/i18next'
import globalStyleSheetUrl from './styles/global.css?url'
import tailwindStyleSheetUrl from './styles/tailwind.css?url'
import { getUserId, signOut } from './utils/auth.server.ts'
import { ClientHintCheck, getHints } from './utils/client-hints.tsx'
import {
  executeCountQuery,
  executeQuery,
  type TrainingProgram,
} from './utils/cms.server.ts'
import { getConfetti } from './utils/confetti.server.ts'
import { prisma } from './utils/db.server.ts'
import { getEnv } from './utils/env.server.ts'
import { siteTitle } from './utils/global.ts'
import { honeypot } from './utils/honeypot.server.ts'
import { combineHeaders, getDomainUrl } from './utils/misc.tsx'
import { useNonce } from './utils/nonce-provider.ts'
import { isSiteAuthEnabled } from './utils/site-auth.server.ts'
import { Status } from './utils/status.ts'
import { makeTimings, time } from './utils/timing.server.ts'
import { getToast } from './utils/toast.server.ts'
import { getActiveEnrollments } from './utils/user.server.ts'

export const middleware = [i18nextMiddleware]

export const links: LinksFunction = () => {
  return [
    // Preload svg sprite as a resource to avoid render blocking
    { rel: 'preload', href: iconsHref, as: 'image' },
    // Preload CSS as a resource to avoid render blocking
    { rel: 'preload', href: tailwindStyleSheetUrl, as: 'style' },
    {
      rel: 'alternate icon',
      type: 'image/png',
      href: '/favicons/favicon-32x32.png',
    },
    { rel: 'apple-touch-icon', href: '/favicons/apple-touch-icon.png' },
    {
      rel: 'manifest',
      href: '/site.webmanifest',
      crossOrigin: 'use-credentials',
    } as const, // necessary to make typescript happy
    //These should match the css preloads above to avoid css as render blocking resource
    { rel: 'icon', type: 'image/svg+xml', href: '/favicons/favicon.svg' },
    { rel: 'stylesheet', href: tailwindStyleSheetUrl },
    { rel: 'stylesheet', href: globalStyleSheetUrl },
    {
      rel: 'stylesheet',
      href: 'https://cdn.jsdelivr.net/npm/swiper@12/swiper-bundle.min.css',
    },
  ].filter(Boolean)
}

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  return [
    { title: data ? siteTitle : `Error | ${siteTitle}` },
    {
      name: 'description',
      content:
        'Training and certification program to prepare educators to teach young children a second language',
    },
  ]
}

export async function loader({ context, request }: LoaderFunctionArgs) {
  const timings = makeTimings('root loader')
  const userId = await time(() => getUserId(request), {
    timings,
    type: 'getUserId',
    desc: 'getUserId in root',
  })

  const user = userId
    ? await time(
        () =>
          prisma.user.findUniqueOrThrow({
            select: {
              id: true,
              firstName: true,
              lastName: true,
              email: true,
              locale: true,
              image: { select: { id: true } },
              roles: {
                select: {
                  name: true,
                  permissions: {
                    select: { entity: true, action: true, access: true },
                  },
                },
              },
            },
            where: { id: userId },
          }),
        { timings, type: 'find user', desc: 'find user in root' },
      )
    : null

  if (userId && !user) {
    console.info('something weird happened')
    // something weird happened... The user is authenticated but we can't find
    // them in the database. Maybe they were deleted? Let's log them out.
    await signOut({ request, redirectTo: '/' })
  }

  const errorData = {
    title: t('errors.general.title'),
    description: t('errors.general.description'),
  }

  let enrolledTrainingPrograms: TrainingProgram[] = []

  if (userId) {
    const activeEnrollments = await getActiveEnrollments(userId)
    const trainingProgramIds = activeEnrollments.map(e => e.trainingProgramId)
    const trainingProgramQuery = `*[_type == 'trainingProgram' && _id in ${JSON.stringify(trainingProgramIds)}]{_id, slug, title}`
    const trainingProgramResult = await executeQuery(trainingProgramQuery)
    if (trainingProgramResult.status !== Status.Success) {
      throw data(errorData, { status: 500 })
    }

    enrolledTrainingPrograms = trainingProgramResult.data as TrainingProgram[]
    if (!enrolledTrainingPrograms) {
      throw data(errorData, { status: 500 })
    }
  }

  // Determine whether to show the FAQ link
  const faqFilter = userId ? 'users == true' : 'marketing == true'
  const faqCountQuery = `count(*[_type == 'frequentlyAskedQuestion' && ${faqFilter}])`
  const faqCountResult = await executeCountQuery(faqCountQuery)
  if (faqCountResult.status !== Status.Success) {
    throw data(errorData, { status: 500 })
  }

  const showFaqLink = faqCountResult.data > 0

  const detectedLocale = getLocale(context)
  // Default to logged in user's saved lang choice, then fall back to i18next detection
  const locale = user?.locale || detectedLocale

  const { toast, headers: toastHeaders } = await getToast(request)
  const { confettiId, headers: confettiHeaders } = getConfetti(request)
  const honeyProps = await honeypot.getInputProps()

  return data(
    {
      user,
      enrolledTrainingPrograms,
      showFaqLink,
      requestInfo: {
        hints: getHints(request),
        origin: getDomainUrl(request),
        path: new URL(request.url).pathname,
      },
      ENV: getEnv(),
      locale,
      toast,
      confettiId,
      honeyProps,
    },
    {
      headers: combineHeaders(
        { 'Set-Cookie': await localeCookie.serialize(locale) },
        { 'Server-Timing': timings.toString() },
        toastHeaders,
        confettiHeaders,
      ),
    },
  )
}

export const headers: HeadersFunction = ({ loaderHeaders }) => {
  const headers: Record<string, string> = {
    'Server-Timing': loaderHeaders.get('Server-Timing') ?? '',
  }

  if (isSiteAuthEnabled()) {
    headers['WWW-Authenticate'] = 'Basic'
  }

  return headers
}

function Document({
  children,
  nonce,
  lang = 'en',
  dir = 'ltr',
  env = {},
}: {
  children: React.ReactNode
  nonce: string
  lang?: string
  dir?: 'ltr' | 'rtl'
  env?: Record<string, string>
}) {
  const location = useLocation()
  const useLightBlueBackgroundGradient =
    location.pathname.includes('terms') ||
    location.pathname.includes('privacy') ||
    location.pathname.includes('signup') ||
    location.pathname.includes('sign-in') ||
    location.pathname.includes('invalid-code') ||
    location.pathname.includes('forgot-password') ||
    location.pathname.includes('reset-password') ||
    location.pathname.includes('faq') ||
    location.pathname.includes('dashboard') ||
    location.pathname.includes('enrollments')

  const useLessonsBackgroundGradient = location.pathname.includes('lessons')

  const useHomeBackground = location.pathname === '/'

  const useOurStoryBackgroundGradient = location.pathname.includes('our-story')

  const bodyStyles: React.CSSProperties = {}
  if (useLightBlueBackgroundGradient) {
    bodyStyles['background'] =
      'linear-gradient(69.97deg, #EAF6FB 5.12%, #F3F9FC 94.01%) fixed'
  }

  if (useLessonsBackgroundGradient) {
    bodyStyles['background'] =
      'linear-gradient(180deg, #FFFAF7 0%, #F9F1FE 100%) fixed'
  }

  if (useHomeBackground) {
    bodyStyles['background'] =
      'linear-gradient(180deg, #E7F9FB 720px, #D8F3F6) fixed'
  }

  if (useOurStoryBackgroundGradient) {
    bodyStyles['background'] =
      'linear-gradient(180deg, #FFF9ED 0%, #FCE5F3 100%) fixed'
  }

  return (
    <html lang={lang} dir={dir} className={`light h-full overflow-x-hidden`}>
      <head>
        <ClientHintCheck nonce={nonce} />
        <Meta />
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width,initial-scale=1" />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link
          rel="preconnect"
          href="https://fonts.gstatic.com"
          crossOrigin="anonymous"
        />
        <link
          href="https://fonts.googleapis.com/css2?family=Poppins:ital,wght@0,300;0,400;0,500;0,700;1,300;1,400;1,500;1,700&display=swap"
          rel="stylesheet"
        />
        <link
          href="https://fonts.googleapis.com/css2?family=Open+Sans:ital,wght@0,300..800;1,300..800&display=swap"
          rel="stylesheet"
        />
        <Links />
      </head>
      <body className="bg-background text-foreground" style={bodyStyles}>
        <GoogleAnalytics />
        {useHomeBackground && (
          <div className="wave-container h-[563px] lg:h-[675px]" />
        )}
        {children}
        <script
          nonce={nonce}
          dangerouslySetInnerHTML={{
            __html: `window.ENV = ${JSON.stringify(env)}`,
          }}
        />
        <ScrollRestoration nonce={nonce} />
        <Scripts nonce={nonce} />
      </body>
    </html>
  )
}

function App() {
  const data = useLoaderData<typeof loader>()
  const nonce = useNonce()

  const { locale } = data
  const { i18n } = useTranslation()

  useEffect(() => {
    const changeLanguage = async () => {
      if (i18n.language !== locale) {
        await i18n.changeLanguage(locale)
      }
    }

    changeLanguage().catch(console.error)
  }, [locale, i18n])

  return (
    <Document
      nonce={nonce}
      lang={i18n.language}
      dir={i18n.dir(i18n.language)}
      env={data.ENV}
    >
      <div className="flex h-screen w-screen flex-col justify-between">
        <a className="sr-only" href="#main-content">
          Skip to main content
        </a>
        <AppHeader
          enrolledTrainingPrograms={data.enrolledTrainingPrograms}
          showFaqLink={data.showFaqLink}
        />

        <div className="flex-1">
          <Outlet />
        </div>

        <Footer />
      </div>
      <Confetti id={data.confettiId} />
      <EpicToaster toast={data.toast} />
      <EpicProgress />
    </Document>
  )
}

function AppWithProviders() {
  const data = useLoaderData<typeof loader>()
  return (
    <HoneypotProvider {...data.honeyProps}>
      <App />
    </HoneypotProvider>
  )
}

export default AppWithProviders

export function ErrorBoundary() {
  // the nonce doesn't rely on the loader so we can access that
  const nonce = useNonce()

  // NOTE: you cannot use useLoaderData in an ErrorBoundary because the loader
  // likely failed to run so we have to do the best we can.
  // We could probably do better than this (it's possible the loader did run).
  // This would require a change in Remix.

  // Just make sure your root route never errors out and you'll always be able
  // to give the user a better UX.

  return (
    <Document nonce={nonce}>
      <GeneralErrorBoundary />
    </Document>
  )
}
